/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import * as cashRegisterService from '../services/cash-register.service';
import type {
  RegisterListItem,
  RegisterDetail,
  RegisterStats,
  ActiveRegister,
  CreateRegisterRequest,
  UpdateRegisterRequest,
  OpenRegisterRequest,
  CloseRegisterRequest,
  ReconcileRegisterRequest,
  CashInOutRequest,
  RegisterListParams,
} from '../types/cash-bank';
import type { PagedResult } from '../types/product';
import type { ApiError } from '@/services/api/types';

// ── Query key factory ─────────────────────────────────────────────────────────

export const registerKeys = {
  all:     ['venta', 'cash-registers'] as const,
  lists:   () => [...registerKeys.all, 'list'] as const,
  list:    (params: RegisterListParams) => [...registerKeys.lists(), params] as const,
  details: () => [...registerKeys.all, 'detail'] as const,
  detail:  (id: string) => [...registerKeys.details(), id] as const,
  stats:   () => [...registerKeys.all, 'stats'] as const,
  active:  () => [...registerKeys.all, 'active'] as const,
  mine:    () => [...registerKeys.all, 'mine'] as const,
};

// ── Queries ───────────────────────────────────────────────────────────────────

export function useCashRegisters(params: RegisterListParams = {}) {
  return useQuery<PagedResult<RegisterListItem>, ApiError>({
    queryKey: registerKeys.list(params),
    queryFn: () => cashRegisterService.getRegisters(params),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function useCashRegister(id: string | undefined) {
  return useQuery<RegisterDetail, ApiError>({
    queryKey: registerKeys.detail(id!),
    queryFn: () => cashRegisterService.getRegister(id!),
    enabled: !!id,
  });
}

export function useCashRegisterStats() {
  return useQuery<RegisterStats, ApiError>({
    queryKey: registerKeys.stats(),
    queryFn: cashRegisterService.getRegisterStats,
    staleTime: 30_000,
  });
}

export function useActiveRegisters() {
  return useQuery<ActiveRegister[], ApiError>({
    queryKey: registerKeys.active(),
    queryFn: cashRegisterService.getActiveRegisters,
    staleTime: 60_000,
  });
}

export function useMyRegister() {
  return useQuery<ActiveRegister | null, ApiError>({
    queryKey: registerKeys.mine(),
    queryFn: cashRegisterService.getMyRegister,
    staleTime: 30_000,
  });
}

// ── Mutations ─────────────────────────────────────────────────────────────────

export function useCreateRegister() {
  const queryClient = useQueryClient();
  return useMutation<RegisterListItem, ApiError, CreateRegisterRequest>({
    mutationFn: cashRegisterService.createRegister,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: registerKeys.lists() });
      queryClient.invalidateQueries({ queryKey: registerKeys.stats() });
      queryClient.invalidateQueries({ queryKey: registerKeys.active() });
    },
  });
}

export function useUpdateRegister() {
  const queryClient = useQueryClient();
  return useMutation<RegisterListItem, ApiError, { id: string; data: UpdateRegisterRequest }>({
    mutationFn: ({ id, data }) => cashRegisterService.updateRegister(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: registerKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: registerKeys.lists() });
    },
  });
}

export function useDeleteRegister() {
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: cashRegisterService.deleteRegister,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: registerKeys.lists() });
      queryClient.invalidateQueries({ queryKey: registerKeys.stats() });
      queryClient.invalidateQueries({ queryKey: registerKeys.active() });
    },
  });
}

export function useOpenRegister() {
  const queryClient = useQueryClient();
  return useMutation<RegisterDetail, ApiError, { id: string; data: OpenRegisterRequest }>({
    mutationFn: ({ id, data }) => cashRegisterService.openRegister(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: registerKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: registerKeys.lists() });
      queryClient.invalidateQueries({ queryKey: registerKeys.stats() });
      queryClient.invalidateQueries({ queryKey: registerKeys.active() });
      queryClient.invalidateQueries({ queryKey: registerKeys.mine() });
    },
  });
}

export function useCloseRegister() {
  const queryClient = useQueryClient();
  return useMutation<RegisterDetail, ApiError, { id: string; data: CloseRegisterRequest }>({
    mutationFn: ({ id, data }) => cashRegisterService.closeRegister(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: registerKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: registerKeys.lists() });
      queryClient.invalidateQueries({ queryKey: registerKeys.stats() });
      queryClient.invalidateQueries({ queryKey: registerKeys.active() });
      queryClient.invalidateQueries({ queryKey: registerKeys.mine() });
    },
  });
}

export function useReconcileRegister() {
  const queryClient = useQueryClient();
  return useMutation<RegisterDetail, ApiError, { id: string; data: ReconcileRegisterRequest }>({
    mutationFn: ({ id, data }) => cashRegisterService.reconcileRegister(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: registerKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: registerKeys.lists() });
      queryClient.invalidateQueries({ queryKey: registerKeys.stats() });
    },
  });
}

export function useCashIn() {
  const queryClient = useQueryClient();
  return useMutation<RegisterDetail, ApiError, { id: string; data: CashInOutRequest }>({
    mutationFn: ({ id, data }) => cashRegisterService.cashIn(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: registerKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: registerKeys.lists() });
      queryClient.invalidateQueries({ queryKey: registerKeys.stats() });
    },
  });
}

export function useCashOut() {
  const queryClient = useQueryClient();
  return useMutation<RegisterDetail, ApiError, { id: string; data: CashInOutRequest }>({
    mutationFn: ({ id, data }) => cashRegisterService.cashOut(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: registerKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: registerKeys.lists() });
      queryClient.invalidateQueries({ queryKey: registerKeys.stats() });
    },
  });
}
